import { getUserInfo } from './auth';

/**
 * 数据门户菜单信号的 localStorage 缓存。
 *
 * 菜单显隐的**运行时**来源是 `store/dataPortalStore`（zustand，订阅式，信号异步到达会自动重渲染）。
 * 这份缓存只做一件事：让 store 在页面刷新后能**同步 hydrate 出首帧**，
 * 避免先渲染一个没有「数据门户」的菜单再闪一下补上。
 */
const getPagesKey = (userId: string) => `swiftx_data_portal_pages_${userId}`;
const getGroupsKey = (userId: string) => `swiftx_data_portal_groups_${userId}`;
const getEnabledKey = (userId: string) => `swiftx_data_portal_enabled_${userId}`;
const getCanConfigKey = (userId: string) => `swiftx_data_portal_can_config_${userId}`;
const getPortalCodesKey = (userId: string) => `swiftx_data_portal_portal_codes_${userId}`;

/** 灰度开关 configKey，与后端 ConfigKey.DATA_PORTAL_EMBED_ENABLE 一致 */
export const DATA_PORTAL_EMBED_ENABLE_KEY = 'data.portal.embed.enable';

/**
 * 二级栏渲染需要的看板元数据。
 *
 * **不含 embedUrl**：嵌入 URL 是免登凭证，不整批存进 localStorage；
 * 打开某个看板时由 `EmbedPage` 用 `DATA_PORTAL_EMBED_URL` 单条取。
 */
export interface MyDataPortalPage {
  pageKey: string;
  nameEn: string;
  nameZh: string;
  nameEs: string;
  /** 所属分组ID，null=未分组（未分组是虚拟分组，前端固定渲染在末位） */
  groupId?: string | null;
  icon?: string | null;
  sortOrder: number;
  openMode: string;
  /** 来源门户 code。未分组看板靠它归到对应门户段（有分组的看板门户由分组隐含） */
  portalCode: string;
}

/** 二级栏的分组行。`enabledPageCount` 只算启用中的看板，与展开内容口径一致 */
export interface MyDataPortalGroup {
  id: string;
  nameEn: string;
  nameZh: string;
  nameEs: string;
  sortOrder: number;
  enabledPageCount: number;
  /** 所属门户 code。分组绑定唯一门户，据此挂到对应门户段下 */
  portalCode: string;
}

function getKeyForCurrentUser(): string | null {
  const id = getUserInfo()?.account?.id;
  return id != null && String(id) !== '' ? String(id) : null;
}

/** 写入当前用户可见的看板列表（与后端 myDataPortalMenu.pages 一致） */
export function saveMyDataPortalPages(userId: string, pages: MyDataPortalPage[]): void {
  localStorage.setItem(getPagesKey(userId), JSON.stringify(pages));
}

export function getMyDataPortalPages(): MyDataPortalPage[] | null {
  const key = getKeyForCurrentUser();
  if (key == null) return null;
  try {
    const raw = localStorage.getItem(getPagesKey(key));
    if (raw == null) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : null;
  } catch {
    return null;
  }
}

export function saveMyDataPortalGroups(userId: string, groups: MyDataPortalGroup[]): void {
  localStorage.setItem(getGroupsKey(userId), JSON.stringify(groups));
}

export function getMyDataPortalGroups(): MyDataPortalGroup[] | null {
  const key = getKeyForCurrentUser();
  if (key == null) return null;
  try {
    const raw = localStorage.getItem(getGroupsKey(key));
    if (raw == null) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : null;
  } catch {
    return null;
  }
}

/**
 * 写入当前用户可见的门户 code 列表（与后端 myDataPortalMenu.portalCodes 一致，已排好序）。
 *
 * **空列表 = 不展示门户分层**，二级栏按「分组 → 看板」两层渲染。
 * 只有配置白名单内的人拿到非空值，见后端 DataPortalApplicationImpl.myDataPortalMenu。
 */
export function saveMyDataPortalPortalCodes(userId: string, codes: string[]): void {
  localStorage.setItem(getPortalCodesKey(userId), JSON.stringify(codes));
}

export function getMyDataPortalPortalCodes(): string[] | null {
  const key = getKeyForCurrentUser();
  if (key == null) return null;
  try {
    const raw = localStorage.getItem(getPortalCodesKey(key));
    if (raw == null) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : null;
  } catch {
    return null;
  }
}

/** 写入灰度开关结果（来自 batchGetConfigEnable） */
export function saveDataPortalEnabled(userId: string, enabled: boolean): void {
  localStorage.setItem(getEnabledKey(userId), enabled ? '1' : '0');
}

/** 灰度是否对当前用户开启。未拉取过时按关闭处理，避免漏配就放量 */
export function isDataPortalEnabled(): boolean {
  const key = getKeyForCurrentUser();
  if (key == null) return false;
  return localStorage.getItem(getEnabledKey(key)) === '1';
}

/** 写入配置权限判定结果（来自 batchGetConfigEnable） */
export function saveDataPortalCanConfig(userId: string, canConfig: boolean): void {
  localStorage.setItem(getCanConfigKey(userId), canConfig ? '1' : '0');
}

/**
 * 当前用户是否在配置白名单内。未拉取过时按无权限处理，与灰度同样的保守默认。
 *
 * **这不是授权依据，只决定入口显不显示。** 真正的判定每次请求都在后端做
 * （`ensureConfigAdmin`）：手改这里的值、或权限被撤销后没刷新页面，
 * 最多得到一个点进去就报 `DATA_PORTAL_CONFIG_FORBIDDEN` 的空入口，拿不到数据。
 *
 * 刷新时机：页面加载（App）、登录成功后（LoginPage）、进入数据门户模块（DataPortalLayout），
 * 三处都走 network-only。加白名单后不需要重新登录，刷新一次即可生效。
 */
export function canConfigDataPortal(): boolean {
  const key = getKeyForCurrentUser();
  if (key == null) return false;
  return localStorage.getItem(getCanConfigKey(key)) === '1';
}

export function removeMyDataPortalPages(): void {
  const key = getKeyForCurrentUser();
  if (key == null) return;
  localStorage.removeItem(getPagesKey(key));
  localStorage.removeItem(getGroupsKey(key));
  localStorage.removeItem(getEnabledKey(key));
  localStorage.removeItem(getCanConfigKey(key));
  localStorage.removeItem(getPortalCodesKey(key));
}

/**
 * 按当前语言取门户展示名。
 *
 * 门户名不由后端下发，走 i18n 的 `enum.dataPortalSource.<CODE>`（三语齐全）。
 * `defaultValue: code` 兜底：漏配某个门户的文案时显示成 code（如 `PRODUCT`）而不是空白，
 * 页面不会因此错乱，加文案后自动恢复。
 *
 * 抽成函数是因为有三处以上调用方（门户分段标题、看板卡片、配置页下拉），
 * 各自拼 `t(\`enum.dataPortalSource.${code}\`)` 容易漏掉 defaultValue。
 */
export function pickPortalName(
  code: string,
  t: (key: string, options?: { defaultValue?: string }) => string,
): string {
  return t(`enum.dataPortalSource.${code}`, { defaultValue: code });
}

/**
 * 按当前语言取菜单名称。
 *
 * 三语只要求至少填一个，所以必须有回退链：先取当前语言对应的名称，
 * 为空时按 **en > zh > es** 依次回退（D8 修订）。
 * lang 传 i18n.language，可能是 'zh-CN' / 'en-US' 这类变体。
 */
export function pickPageName(
  page: Pick<MyDataPortalPage, 'nameEn' | 'nameZh' | 'nameEs'>,
  lang: string | undefined,
): string {
  const normalized = (lang ?? 'en').toLowerCase().split('-')[0];
  const current =
    normalized === 'zh' ? page.nameZh : normalized === 'es' ? page.nameEs : page.nameEn;
  return (
    current?.trim() ||
    page.nameEn?.trim() ||
    page.nameZh?.trim() ||
    page.nameEs?.trim() ||
    ''
  );
}
